import {
  CONTRACT_TYPE_LABELS,
  DO_CONTRACT_TYPES,
  INTERVENTION_DATE_MAX,
  INTERVENTION_DATE_MIN,
} from "@etudescarre/constants";
import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { captureException } from "@sentry/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import {
  DocumentTable,
  type DocumentTableProps,
} from "../components/document-table/document-table";
import type { DocumentData } from "../components/document-table/types";
import { useDocumentActions } from "../components/document-table/use-document-actions";
import {
  BuildingNav,
  interventionLots,
  ZoneCard,
} from "../components/study-progress";
import { AvancementEtudesContrat } from "./avancement-etudes-contrat";
import { AvancementEtudesDo } from "./avancement-etudes-do";

type ZoneSummary = {
  id: number;
  name: string;
  shortName: string | null;
  type: "horizontale" | "verticale";
  interventionCount: number;
  withoutDocumentCount: number;
  producedDocumentCount: number;
  latestBpeDocumentCount: number;
  forecastDocumentCount: number;
};
type BuildingSummary = {
  id: number;
  name: string;
  shortName: string | null;
  zones: ZoneSummary[];
};
type FilterOption = { id: number; name: string };
type SummaryData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  buildings: BuildingSummary[];
};
type ProviderContract = {
  id: number;
  label: string;
  companyName: string;
  type: "travaux_n_1" | "travaux_n_2";
};
type StudyIntervention = {
  id: number;
  dateIntervention: string | null;
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  providerContracts: ProviderContract[];
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: SummaryData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; zoneId: number; interventions: StudyIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

const doContractTypes: readonly string[] = DO_CONTRACT_TYPES;

const PROVIDER_RANK_LABELS: Record<ProviderContract["type"], string> = {
  travaux_n_1: CONTRACT_TYPE_LABELS.travaux_n_1,
  travaux_n_2: CONTRACT_TYPE_LABELS.travaux_n_2,
};

/**
 * Aiguillage par type de contrat de la société de l'utilisateur :
 * - Travaux rang 1 → vue pilote (écran 012) ;
 * - Travaux rang 2 uniquement → vue "mon contrat" (écran 034) ;
 * - Aucun contrat travaux mais contrat donneur d'ordre (AMO / CT / SPS / MOE)
 *   → vue donneur d'ordre (écran 025).
 */
export function AvancementEtudes() {
  const { contracts } = useYardContext();
  const hasRankOne = contracts.some(
    (contractItem) => contractItem.type === "travaux_n_1",
  );
  const hasRankTwo = contracts.some(
    (contractItem) => contractItem.type === "travaux_n_2",
  );
  const hasDoContract = contracts.some((contractItem) =>
    doContractTypes.includes(contractItem.type),
  );
  if (!hasRankOne && hasRankTwo) return <AvancementEtudesContrat />;
  if (!hasRankOne && !hasRankTwo && hasDoContract)
    return <AvancementEtudesDo />;
  return <AvancementEtudesPilote />;
}

function AvancementEtudesPilote() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    void summaryRefresh;
    if (isNil(yardId)) return;
    let cancelled = false;
    // Keep the screen in place while it reloads after an action.
    setState((current) =>
      current.status === "success" ? current : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedBuildingId(
          (current) => current ?? data.data.buildings[0]?.id ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery, summaryRefresh]);

  useEffect(() => {
    void detailRefresh;
    if (isNil(yardId) || isNil(selectedZoneId)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail((current) =>
      current.status === "success" && current.zoneId === selectedZoneId
        ? current
        : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/zones/{zoneId}", {
        params: {
          path: { yardId, zoneId: String(selectedZoneId) },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          zoneId: selectedZoneId,
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    selectedZoneId,
    lotQuery,
    performanceQuery,
    recentOnly,
    detailRefresh,
  ]);

  // A success for another zone is still on screen until the effect above
  // runs for the newly selected zone.
  const zoneDetail: DetailState =
    detail.status === "success" && detail.zoneId !== selectedZoneId
      ? { status: "loading" }
      : detail;
  const { tableProps, overlays } = useDocumentActions({
    yardId,
    lots:
      zoneDetail.status === "success"
        ? zoneDetail.interventions.flatMap(interventionLots)
        : [],
    onChanged: () => {
      setSummaryRefresh((value) => value + 1);
      setDetailRefresh((value) => value + 1);
    },
  });

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };

  if (state.status === "loading")
    return <PageMessage text="Chargement de l'avancement des études…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger l'avancement des études."
        error
      />
    );
  if (state.data.buildings.length === 0)
    return (
      <PageMessage text="Aucun bâtiment n'est configuré pour ce chantier." />
    );

  const selectedBuilding =
    state.data.buildings.find((item) => item.id === selectedBuildingId) ??
    state.data.buildings[0];

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-4">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Avancement des études
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez les interventions que vous pilotez par bâtiment et par
          zone.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      <BuildingNav
        buildings={state.data.buildings}
        selectedId={selectedBuilding?.id}
        onSelect={(buildingId) => {
          setSelectedBuildingId(buildingId);
          setSelectedZoneId(null);
        }}
      />
      <main className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto">
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
          {selectedBuilding?.name}
        </h3>
        {selectedBuilding?.zones.length === 0 && (
          <p className="text-sm text-zinc-500">Aucune zone dans ce bâtiment.</p>
        )}
        {selectedBuilding?.zones.map((zoneItem) => (
          <Fragment key={zoneItem.id}>
            <ZoneCard
              name={zoneItem.name}
              counters={[
                `${zoneItem.interventionCount} intervention(s)`,
                `${zoneItem.withoutDocumentCount} sans document`,
                `${zoneItem.latestBpeDocumentCount} document(s) BPE`,
                `${zoneItem.producedDocumentCount} document(s) produit(s)`,
                `${zoneItem.forecastDocumentCount} document(s) prévisionnel(s)`,
              ]}
              open={zoneItem.id === selectedZoneId}
              onToggle={() =>
                setSelectedZoneId(
                  zoneItem.id === selectedZoneId ? null : zoneItem.id,
                )
              }
            />
            {zoneItem.id === selectedZoneId && (
              <ZoneDetail
                state={zoneDetail}
                yardId={yardId ?? ""}
                tableProps={tableProps}
                onDateSaved={() => setDetailRefresh((value) => value + 1)}
              />
            )}
          </Fragment>
        ))}
      </main>
      {overlays}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}
type ZoneDetailProps = {
  state: DetailState;
  yardId: string;
  tableProps: Omit<DocumentTableProps, "lots">;
  onDateSaved: () => void;
};
function ZoneDetail({
  state,
  yardId,
  tableProps,
  onDateSaved,
}: ZoneDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette zone."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => (
        <article key={item.id}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
                {item.lot.nLot ? `Lot ${item.lot.nLot} — ` : ""}
                {item.performance.name}
              </h4>
              {item.providerContracts.length === 0 ? (
                <p className="mt-1 text-sm text-zinc-500">
                  Aucun contrat prestataire actif
                </p>
              ) : (
                item.providerContracts.map((provider) => (
                  <p key={provider.id} className="mt-1 text-sm text-zinc-500">
                    {`${PROVIDER_RANK_LABELS[provider.type]} : ${provider.label} (${provider.companyName})`}
                  </p>
                ))
              )}
            </div>
            <InterventionDateField
              yardId={yardId}
              interventionId={item.id}
              value={item.dateIntervention}
              onSaved={onDateSaved}
            />
          </div>
          <div className="mt-2">
            {item.documents.length === 0 ? (
              <p className="rounded-lg bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                Aucun document produit pour cette intervention.
              </p>
            ) : (
              <DocumentTable
                lots={interventionLots(item)}
                inScrollingList
                {...tableProps}
              />
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function InterventionDateField({
  yardId,
  interventionId,
  value,
  onSaved,
}: {
  yardId: string;
  interventionId: number;
  value: string | null;
  onSaved: () => void;
}) {
  const [currentValue, setCurrentValue] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // The browser reports a date as soon as it parses, 0002 included, while the
  // year is still being typed; a date picked in the calendar is finished.
  const isTyping = useRef(false);
  const errorId = `intervention-date-error-${interventionId}`;

  useEffect(() => setCurrentValue(value ?? ""), [value]);

  const save = async (dateIntervention: string) => {
    setIsSaving(true);
    try {
      const { error: apiError } = await apiClient.PATCH(
        "/api/yards/{yardId}/study-progress/interventions/{interventionId}",
        {
          params: { path: { yardId, interventionId: String(interventionId) } },
          body: { dateIntervention: dateIntervention || null },
        },
      );
      if (isNotNil(apiError)) {
        setError(apiError.error);
        setCurrentValue(value ?? "");
        return;
      }
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
      onSaved();
    } catch (err) {
      console.error("Failed to save intervention date", err);
      captureException(err);
      setError(
        "La date n'est pas enregistrée : le serveur n'a pas pu être joint. Réessayez.",
      );
      setCurrentValue(value ?? "");
    } finally {
      setIsSaving(false);
    }
  };

  const commit = (input: HTMLInputElement) => {
    isTyping.current = false;
    if (!input.validity.valid) {
      setError(
        "Date non enregistrée : saisissez une date complète entre le 01/01/2000 et le 31/12/2099.",
      );
      return;
    }
    setError(null);
    if (input.value !== (value ?? "")) void save(input.value);
  };

  return (
    <div className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
      <label>
        Date d'intervention
        <input
          type="date"
          value={currentValue}
          min={INTERVENTION_DATE_MIN}
          max={INTERVENTION_DATE_MAX}
          readOnly={isSaving}
          aria-invalid={isNotNil(error)}
          aria-describedby={isNotNil(error) ? errorId : undefined}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit(event.currentTarget);
            else isTyping.current = true;
          }}
          onChange={(event) => {
            setCurrentValue(event.target.value);
            if (!isTyping.current) commit(event.currentTarget);
          }}
          onBlur={(event) => commit(event.currentTarget)}
          className="mt-1 block rounded border border-zinc-300 bg-white px-2 py-1 text-sm read-only:opacity-60 aria-invalid:border-red-500 dark:border-zinc-700 dark:bg-zinc-800"
        />
      </label>
      {isSaving && <span className="mt-1 block">Enregistrement…</span>}
      {isNotNil(error) && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 max-w-56 font-normal text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}
